import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";

const blank = () => ({ account_name: "", bsb: "", account_number: "", split_percent: "" });

export function BankAccountsEditor({ personId }) {
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const load = () => api.get(`/people/${personId}/bank-accounts`).then((r) => { setRows(r.data.map((a) => ({ ...a, split_percent: String(a.split_percent ?? "") }))); setDirty(false); });
  useEffect(() => { load(); }, [personId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (i, k, v) => { setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r))); setDirty(true); };
  const total = rows.reduce((s, r) => s + (parseFloat(r.split_percent) || 0), 0);
  const save = async () => {
    setError("");
    try {
      await api.put(`/people/${personId}/bank-accounts`, { accounts: rows.map(({ id, account_name, bsb, account_number, split_percent }) => ({ id, account_name, bsb, account_number, split_percent })) });
      toast.success("Bank accounts saved");
      load();
    } catch (e) { setError(formatError(e)); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5 space-y-3" data-testid="bank-accounts-editor">
      <div className="hidden sm:grid grid-cols-[1.4fr_0.8fr_1fr_0.6fr_40px] gap-2 text-xs uppercase tracking-wider text-slate-400">
        <span>Account name</span><span>BSB</span><span>Account number</span><span>Split %</span><span />
      </div>
      {rows.map((r, i) => (
        <div key={r.id || `new-${i}`} className="grid grid-cols-2 sm:grid-cols-[1.4fr_0.8fr_1fr_0.6fr_40px] gap-2" data-testid={`bank-row-${i}`}>
          <Input disabled={!canWrite} value={r.account_name} onChange={(e) => set(i, "account_name", e.target.value)} placeholder="Account name" className="col-span-2 sm:col-span-1" data-testid={`bank-name-${i}`} />
          <Input disabled={!canWrite} value={r.bsb} onChange={(e) => set(i, "bsb", e.target.value)} placeholder="062-000" className="font-mono" data-testid={`bank-bsb-${i}`} />
          <Input disabled={!canWrite} value={r.account_number} onFocus={() => r.account_number?.startsWith("*") && set(i, "account_number", "")}
            onChange={(e) => set(i, "account_number", e.target.value)} placeholder={r.id ? "Leave blank to keep" : "Account number"} className="font-mono" data-testid={`bank-number-${i}`} />
          <Input disabled={!canWrite} value={r.split_percent} onChange={(e) => set(i, "split_percent", e.target.value)} placeholder="100" className="font-mono" data-testid={`bank-split-${i}`} />
          {canWrite && <Button size="icon" variant="ghost" onClick={() => { setRows(rows.filter((_, j) => j !== i)); setDirty(true); }} data-testid={`bank-remove-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
        </div>
      ))}
      {!rows.length && <p className="text-sm text-slate-400">No bank accounts yet.</p>}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-100">
        <span className={`text-sm font-mono ${rows.length && Math.abs(total - 100) > 0.001 ? "text-red-600" : "text-emerald-700"}`} data-testid="bank-split-total">Total {total}%</span>
        <span className="text-xs text-slate-400">Numbers are encrypted and shown masked. Splits must total 100%.</span>
        {canWrite && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { setRows([...rows, blank()]); setDirty(true); }} data-testid="bank-add-button"><Plus className="h-4 w-4 mr-1" />Add account</Button>
            <Button size="sm" disabled={!dirty} onClick={save} data-testid="bank-save-button">Save</Button>
          </div>
        )}
      </div>
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="bank-error">{error}</p>}
    </div>
  );
}
